import { memo, useEffect } from "react";
import "./sidebar.css";
import { Button, Menu } from "antd";
import { useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../common/utils/useProjectNavigate";
import { Icon } from "../icon/icon";
import SidebarSubjectInfo from "./sidebar-subject-info";
import { useSidebarContext } from "./sidebar-context";

const normalizeRouteKey = (key = "") => {
  if (!key) return key;

  const [pathPart, hashPart = ""] = String(key).split("#");
  const parts = pathPart.split("/");

  let normalizedPath = pathPart;

  // /TP1/subject-management/subject/123 -> /subject-management/subject/123
  if (parts.length > 2) {
    normalizedPath = "/" + parts.slice(2).join("/");
  }

  return `${normalizedPath}${hashPart ? `#${hashPart}` : ""}`;
};

export const Sidebar = memo(
  // `titleTestID`: data-testid of the title, for the browser tests (LF360-1981).
  ({ title, items, onBack, subjectInfo, backOnly = false, titleTestID }) => {
    const { isOpen, setIsOpen, setHasSider } = useSidebarContext();
    const navigate = useProjectNavigate();
    const location = useLocation();
    const { pathname, hash } = location;
    useEffect(() => {
      if (!location.state?.openSidebar) return;
      if (backOnly) return;
      if (window.innerWidth > 1024) return;

      setIsOpen(true);
    }, [location.key, location.state, backOnly]);

    const selectHandler = ({ key }) => {
      navigate(key);
      setIsOpen(false);
    };

    const normalizedPath = (() => {
      const parts = pathname.split("/");

      if (parts.length > 2) {
        return "/" + parts.slice(2).join("/") + (hash || "");
      }

      return pathname + (hash || "");
    })();

    const selectedKey = (() => {
      if (normalizedPath === "/administration") {
        return "/administration/system-users";
      }

      if (normalizedPath === "/content") {
        return "/content/tasks";
      }

      if (normalizedPath === "/subject-management") {
        return "/subject-management#subjects";
      }

      if (normalizedPath === "/analytics") {
        return "/analytics/audit-trail/subjects";
      }

      return normalizedPath;
    })();

    const matchedSelectedKey =
      items?.find((item) => normalizeRouteKey(item.key) === selectedKey)?.key ||
      selectedKey;

    useEffect(() => {
      return () => { setIsOpen(false); };
    }, []);

    // Tell the layout there's a real sidebar mounted so Header can render its
    // hamburger. `backOnly` returns null below, so skip registration in that mode.
    useEffect(() => {
      if (backOnly) return undefined;
      setHasSider(true);
      return () => setHasSider(false);
    }, [backOnly, setHasSider]);

    if (backOnly) {
      return null;
    }

    return (
      <>
        {isOpen && (
          <div
            className="lf-sidebar__backdrop"
            onClick={() => setIsOpen(false)}
          />
        )}
        <div className={`lf-sidebar${isOpen ? " lf-sidebar--open" : ""}`}>
          <div className="lf-sidebar__header">
            {onBack && (
              <Button
                icon={<Icon name="arrow-left" />}
                style={{ marginRight: 16, marginLeft: -18 }}
                onClick={onBack}
              />
            )}
            <h2 data-testid={titleTestID}>{title}</h2>
          </div>

          <div className="lf-sidebar__body">
            {subjectInfo && (
              <div className="lf-sidebar__subject-info">
                <SidebarSubjectInfo {...subjectInfo} />
              </div>
            )}

            <Menu
              selectedKeys={[matchedSelectedKey]}
              items={items}
              onSelect={selectHandler}
            />
          </div>
        </div>
      </>
    );
  }
);
